// Minimap: the splash plan in miniature, north up, with the stops, the GPS route and the scooter.
import { S } from '../game/ride.js';
import { TOUCH } from '../game/input.js';
import { visited, nextStop } from '../game/stop-markers.js';
import { STOPS } from '../data/stops.js';
import { tr } from './lang.js';
import { state } from '../core/state.js';

export let updateMinimap;

const PAL = {
  day: {
    sea: '#c4d8d6',
    land: '#efe6d2',
    sand: '#ead7a8',
    walk: '#e4d8bf',
    block: '#d9b48f',
    green: '#b9cf94',
    ink: '#3b3226',
  },
  night: {
    sea: '#0f1a33',
    land: '#1d2742',
    sand: '#2b3150',
    walk: '#252f4d',
    block: '#33395c',
    green: '#1f3b3a',
    ink: '#9fb0dc',
  },
};
// stop colours are 0xRRGGBB numbers; canvas wants CSS strings
const css = (c) => '#' + c.toString(16).padStart(6, '0');
const VIEW = 80; // world units from the centre to the rim, so every stop fits inside the circle

function octPath(g, cx, cz, size, ch) {
  const h = size / 2,
    p = [
      [-h + ch, -h],
      [h - ch, -h],
      [h, -h + ch],
      [h, h - ch],
      [h - ch, h],
      [-h + ch, h],
      [-h, h - ch],
      [-h, -h + ch],
    ];
  g.beginPath();
  p.forEach(([x, z], i) => g[i ? 'lineTo' : 'moveTo'](cx + x, cz + z));
  g.closePath();
}

// the static plan in world units; the caller has already scaled and centred the context
function drawPlan(g, C) {
  const E = 65,
    R = 14,
    B = 51;
  g.fillStyle = C.sea;
  g.fillRect(-VIEW, -VIEW, VIEW * 2, VIEW * 2);
  g.beginPath();
  g.moveTo(-E, B);
  g.lineTo(-E, -E + R);
  g.quadraticCurveTo(-E, -E, -E + R, -E);
  g.lineTo(E - R, -E);
  g.quadraticCurveTo(E, -E, E, -E + R);
  g.lineTo(E, B);
  g.closePath();
  g.fillStyle = C.land;
  g.fill();
  g.fillStyle = C.sand;
  g.fillRect(-E, B, 2 * E, 13);
  for (const cx of [-30, 0, 30])
    for (const cz of [-30, 0, 30]) {
      octPath(g, cx, cz, 25.5, 4.8);
      g.fillStyle = C.walk;
      g.fill();
      octPath(g, cx, cz, 22, 4);
      g.fillStyle = C.block;
      g.fill();
      g.lineWidth = 0.9;
      g.strokeStyle = C.ink;
      g.stroke();
      octPath(g, cx, cz, 12, 2);
      g.fillStyle = C.green;
      g.fill();
    }
}

export function buildMinimap() {
  const box = document.createElement('div'),
    cv = document.createElement('canvas'),
    hide = document.createElement('button'),
    show = document.createElement('button');
  box.id = 'minimap';
  hide.className = 'mmHide';
  hide.textContent = '✕';
  show.id = 'minimapShow';
  show.textContent = '🗺';
  box.append(cv, hide);
  document.body.append(box, show);

  // phones start with it folded away: the screen is small and the thumbs need the corners
  let open = !TOUCH;
  try {
    const v = localStorage.getItem('minimap');
    if (v) open = v === '1';
  } catch (e) {}
  const setOpen = (o) => {
    open = o;
    try {
      localStorage.setItem('minimap', o ? '1' : '0');
    } catch (e) {}
  };
  hide.addEventListener('click', () => setOpen(false));
  show.addEventListener('click', () => setOpen(true));
  addEventListener('keydown', (e) => {
    if (e.code === 'KeyM' && state.mode === 'ride' && !state.panelOpen) setOpen(!open);
  });

  let base = null,
    baseKey = '',
    lang = '';
  updateMinimap = function updateMinimap() {
    const riding = state.mode === 'ride' && !state.panelOpen;
    box.style.display = riding && open ? 'block' : 'none';
    show.style.display = riding && !open ? 'block' : 'none';
    if (lang !== state.lang) {
      lang = state.lang;
      hide.title = hide.ariaLabel = tr('mapHide');
      show.title = show.ariaLabel = tr('mapShow');
    }
    if (!riding || !open) return;
    const size = box.clientWidth,
      dpr = Math.min(devicePixelRatio, 2),
      px = Math.round(size * dpr),
      k = px / 2 / VIEW, // device pixels per world unit
      C = state.isNight ? PAL.night : PAL.day;
    if (cv.width !== px) cv.width = cv.height = px;
    // the plan only changes with the size or day/night, so it is drawn once into a cache
    const key = px + (state.isNight ? 'n' : 'd');
    if (key !== baseKey) {
      baseKey = key;
      base = document.createElement('canvas');
      base.width = base.height = px;
      const b = base.getContext('2d');
      b.setTransform(k, 0, 0, k, px / 2, px / 2);
      drawPlan(b, C);
    }
    const g = cv.getContext('2d');
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.drawImage(base, 0, 0);
    g.setTransform(k, 0, 0, k, px / 2, px / 2);
    const n = nextStop();
    if (state.route && n >= 0) {
      g.beginPath();
      state.route.pts.forEach(([x, z], i) => g[i ? 'lineTo' : 'moveTo'](x, z));
      g.lineWidth = 3;
      g.lineJoin = g.lineCap = 'round';
      g.strokeStyle = css(STOPS[n].color);
      g.stroke();
    }
    g.font = '800 6px system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    STOPS.forEach((st, i) => {
      const live = visited.has(i) || i === n;
      g.beginPath();
      g.arc(st.x, st.z, i === n ? 6 : 4.8, 0, Math.PI * 2);
      g.fillStyle = live ? css(st.color) : '#9aa0aa';
      g.fill();
      g.lineWidth = 0.8;
      g.strokeStyle = C.land;
      g.stroke();
      g.fillStyle = '#fff';
      g.fillText(visited.has(i) ? '✓' : i + 1, st.x, st.z + 0.3);
    });
    // the scooter: an arrowhead along its heading (S.th: +z at 0, +x at π/2)
    g.save();
    g.translate(S.x, S.z);
    g.rotate(-S.th);
    g.beginPath();
    g.moveTo(0, 7);
    g.lineTo(5, -5);
    g.lineTo(0, -2.4);
    g.lineTo(-5, -5);
    g.closePath();
    // dark with a white rim, like a GPS position marker, so it never reads as one of the stops
    g.lineWidth = 2.4;
    g.lineJoin = 'round';
    g.strokeStyle = '#fff';
    g.stroke();
    g.fillStyle = '#1d2433';
    g.fill();
    g.restore();
  };
}
